import type { CitationStatus } from '@/lib/types'
import type { CitationListProps } from './CitationList.types'
import { ReferenceList } from './ReferenceList'

// An engine that cannot expose sources reads differently from one that searched and found nothing.
function absenceCopy(status: CitationStatus): string | null {
  switch (status) {
    case 'engine_unsupported':
      return 'This engine does not expose sources.'
    case 'search_not_invoked':
      return 'Answered from prior knowledge — did not search.'
    case 'none_returned':
      return 'Searched, but cited no sources.'
    case 'has_citations':
    case 'not_requested':
      return null
  }
}

export function CitationList({
  citations,
  references,
  status,
}: CitationListProps) {
  if (!status || status === 'not_requested') return null

  const copy = absenceCopy(status)
  const cited = citations ?? []

  // An engine can search, fetch pages, and cite none, so references outlive an empty Sources list.
  if (!copy && !cited.length && !references?.length) return null

  return (
    <div className="mt-2 pt-2 border-t border-border">
      {copy && (
        <div className="text-xs text-muted-foreground italic">{copy}</div>
      )}

      {cited.length > 0 && (
        <>
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Sources
          </div>
          <ul className="space-y-1">
            {cited.map((c) => (
              <li key={c.url} className="text-xs">
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={c.title?.trim() || undefined}
                  className="text-muted-foreground hover:text-foreground underline break-all"
                >
                  {c.domain || c.url}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      <ReferenceList references={references} />
    </div>
  )
}
